/**
 * /submit — path 1: a Sleeper league id.
 *
 * Unchanged behavior from the single-path version of this page; it moved into
 * its own file when the Yahoo/ESPN path arrived so neither form owns the shell.
 */

import { useState } from "react";
import { captureLeagueSubmitted } from "../../utils/submitAnalytics.js";
import { C, SZ, card, input, label, primaryBtn, fineText, successText, errorText } from "./submitStyles.js";

export default function SleeperForm() {
  const [value, setValue] = useState("");
  // One state machine rather than three booleans: idle | submitting | success |
  // error. There is deliberately NO duplicate phase — see submit() below.
  // `result` carries the league name / error message.
  const [phase, setPhase] = useState("idle");
  const [result, setResult] = useState(null);

  const busy = phase === "submitting";
  const canSubmit = !busy && value.trim().length > 0;

  async function submit() {
    if (!canSubmit) return;
    setPhase("submitting");
    setResult(null);
    try {
      const resp = await fetch("/api/submit-league", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ input: value.trim(), source: "submit_page" }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.ok) {
        setResult({ error: data.error || "Something went wrong. Try again in a moment." });
        setPhase("error");
        return;
      }
      setResult({ leagueName: data.leagueName, season: data.season });
      // 🔴 A DUPLICATE IS DELIBERATELY INDISTINGUISHABLE FROM A NEW SUBMISSION.
      // The API still answers { duplicate: true } and the analytics event still
      // carries it — that split is internal. It must not reach the screen: a
      // "we already have that one" message tells a visitor whether some other
      // person has already submitted their league, which is a fact about
      // someone else's behavior, and it makes a helpful act read as wasted.
      // Every accepted paste gets the same confirmation.
      setPhase("success");
      captureLeagueSubmitted({ season: data.season, duplicate: !!data.duplicate });
    } catch {
      // Offline, or the endpoint is unreachable. Same affordance either way:
      // the input keeps its value so a retry is one tap.
      setResult({ error: "Could not reach the server. Check your connection and try again." });
      setPhase("error");
    }
  }

  const done = phase === "success";

  return (
    <div style={card}>
      {done ? (
        <div style={successText}>
          {`Got it — ${result?.leagueName || "your league"} is in the dataset.`}
        </div>
      ) : (
        <>
          <label htmlFor="submit-league-input" style={label}>
            Sleeper league URL or league ID
          </label>
          {/* Column on phones by default; the button sits beside the input only
              where there is room for it. */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <input
              id="submit-league-input"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              disabled={busy}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="sleeper.com/leagues/… or 1180389865513353216"
              style={{ ...input, flex: "1 1 220px", width: "auto" }}
            />
            <button onClick={submit} disabled={!canSubmit} style={{ ...primaryBtn(canSubmit), flex: "1 1 120px" }}>
              {busy ? "Submitting…" : "Add my league"}
            </button>
          </div>
          {phase === "error" && <div style={errorText}>{result?.error}</div>}
        </>
      )}

      <div style={fineText}>
        Completed drafts are public on Sleeper's API. Leagues are used in aggregate only and nothing
        identifying is ever shown.
      </div>

      <div style={{ marginTop: 14, fontSize: SZ.small, color: C.text.secondary, lineHeight: 1.5 }}>
        Drafting on another platform? Track your auction with the{" "}
        <a href="/live" style={{ color: C.brand.accent, fontWeight: 700 }}>
          Live Board
        </a>{" "}
        and it joins the dataset automatically.
      </div>
    </div>
  );
}
